import React, { useLayoutEffect } from 'react';
import {
  FileCardsGrid,
  RequestAttentionRequestedAlert,
  RequestExtrasCard,
  RequestExtrasCardContent,
  RequestMediaSourceCard,
  RequestMediaSourceCardContent,
  RequestPageContainer,
  RequestPageContentMain,
} from 'src/pages/request/request-page/request-default-page';
import { cn } from 'src/lib/utils';
import { Skeleton } from 'src/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { If } from 'src/components/If';
import { useRequestId } from 'src/features/requests/use-request-id';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { isClientDeleted } from 'src/features/requests/request.helper';
import { RequestDeletedByClientBanner } from 'src/features/requests/request-deleted-by-client-banner';
import {
  RequestDeliverableOutcomesGrid,
  RequestOutcomesSummaryCard,
  RequestStrategyCard,
} from 'src/features/requests/request-page/multi-outcome';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { CollapsibleCard, CollapsibleCardContent } from 'src/components/ui/collapsible-card';
import { useToggle } from 'src/lib/state-utils';
import { FolderOpen } from 'lucide-react';
import { Label } from 'src/components/ui/label';
import dompurify from 'dompurify';
import Str from 'src/helpers/Str';

// Multi-outcome parents have no project-level type/length/priority/etc — those
// live per-outcome — so the Summary tab just surfaces the project name here
// and defers the per-outcome detail to RequestOutcomesSummaryCard below. The
// client-provided brief is still project-level though, so it's shown here too.
const ProjectTitleCard: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useToggle(true);
  const request = useDecoratedRequestContext();

  return (
    <CollapsibleCard
      icon={<FolderOpen />}
      title={t('common:project_title', { defaultValue: 'Project title' })!}
      open={open}
      onOpenChange={setOpen}
      variant={'shadow'}
    >
      <CollapsibleCardContent className={'tw-flex tw-flex-col tw-gap-4'}>
        <p className={'tw-break-words tw-text-base tw-font-medium tw-text-text-strong'}>
          {request.title}
        </p>

        <div>
          <Label size={'lg'} className={'tw-mb-2'}>
            {t('common:description', { defaultValue: 'Description' })}
          </Label>
          <p
            className={'tw-whitespace-pre-line tw-break-words'}
            dangerouslySetInnerHTML={{
              __html: Str.wrapHrefs(
                dompurify.sanitize(
                  request.description?.video_request_description ??
                    t('common:unset', { defaultValue: '-' })!,
                ),
              ),
            }}
          />
        </div>
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
};

const RequestMultiOutcomeSkeleton: React.FC<React.ComponentProps<typeof Skeleton>> = ({
  className,
  ...props
}) => {
  return (
    <RequestPageContainer {...props} className={cn('tw-col-span-12', className)}>
      <Skeleton className="tw-h-10 tw-w-1/3 tw-rounded-md" />
      <RequestPageContentMain className={'tw-grid-cols-12'}>
        <Skeleton className="tw-col-span-12 tw-h-full md:tw-col-span-8 lg:tw-col-span-9" />
        <Skeleton className="tw-col-span-12 tw-h-full md:tw-col-span-4 lg:tw-col-span-3" />
      </RequestPageContentMain>
    </RequestPageContainer>
  );
};

const knownMultiOutcomeTabs = ['deliverables', 'summary'] as const;

const MainContentTabsList: React.FC<
  React.ComponentProps<typeof TabsList> & {
    setActiveTab: React.Dispatch<React.SetStateAction<string>>;
    activeTab: string;
  }
> = ({ setActiveTab, activeTab, ...props }) => {
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (!knownMultiOutcomeTabs.includes(activeTab as (typeof knownMultiOutcomeTabs)[number])) {
      setActiveTab('deliverables');
    }
  }, [activeTab]);

  return (
    <TabsList className={'tw-h-auto'} {...props}>
      <TabsTrigger className={'tw-rounded-lg tw-px-4 tw-py-3'} value={'deliverables'}>
        {t('common:deliverables', {
          defaultValue: 'Deliverables',
        })}
      </TabsTrigger>

      <TabsTrigger className={'tw-rounded-lg tw-px-4 tw-py-3'} value={'summary'}>
        {t('common:summary', {
          defaultValue: 'Summary',
        })}
      </TabsTrigger>
    </TabsList>
  );
};

const RequestMultiOutcomePageMainContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => {
  const requestId = useRequestId();
  const [activeTab, setActiveTab] = useQueryParam<string>(
    'tab',
    withDefault(StringParam, 'deliverables'),
    { updateType: 'replaceIn' },
  );
  const request = useDecoratedRequestContext();
  const { t } = useTranslation();

  return (
    <div ref={ref} {...props}>
      {isClientDeleted(request) && <RequestDeletedByClientBanner request={request} />}
      <Tabs
        onValueChange={(value) => setActiveTab(value)}
        defaultValue={activeTab}
        value={activeTab}
        className={'tw-flex tw-h-full tw-flex-col tw-gap-2'}
      >
        <MainContentTabsList
          setActiveTab={setActiveTab}
          activeTab={activeTab}
          className={
            'tw-justify-start tw-gap-2 tw-overflow-x-auto tw-overflow-y-clip tw-border-0 tw-bg-transparent'
          }
        />

        <TabsContent className={'tw-relative tw-mt-0 tw-grow'} value={'summary'}>
          <div className={'tw-absolute tw-inset-0 tw-flex tw-flex-col tw-gap-4'}>
            <RequestAttentionRequestedAlert />
            <ScrollArea>
              <div className={'tw-flex tw-flex-col tw-gap-4 tw-px-1 tw-pb-1'}>
                <ProjectTitleCard />

                <RequestExtrasCard>
                  <RequestExtrasCardContent />
                </RequestExtrasCard>

                <RequestStrategyCard />

                <RequestMediaSourceCard>
                  <RequestMediaSourceCardContent>
                    <If when={request.hasMedia} else={t('common:unset', { defaultValue: '-' })}>
                      <FileCardsGrid
                        className={
                          'tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 desktop:tw-grid-cols-4'
                        }
                        files={request.media}
                      />
                    </If>
                  </RequestMediaSourceCardContent>
                </RequestMediaSourceCard>

                <RequestOutcomesSummaryCard />
              </div>
            </ScrollArea>
          </div>
        </TabsContent>

        <TabsContent className={'tw-relative tw-mt-0 tw-grow'} value={'deliverables'}>
          <div className={'tw-absolute tw-inset-0 tw-flex tw-flex-col tw-gap-4'}>
            <RequestAttentionRequestedAlert />

            <div
              className={
                'tw-flex tw-h-full tw-flex-col tw-overflow-auto tw-rounded-lg tw-border tw-bg-white tw-p-4 md:tw-p-4'
              }
            >
              <RequestDeliverableOutcomesGrid requestId={requestId} />
            </div>
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
});
RequestMultiOutcomePageMainContent.displayName = 'RequestDefaultPageMainContent';

export { RequestMultiOutcomeSkeleton, RequestMultiOutcomePageMainContent };
